import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  Home, Receipt, Settings, Plus, Calendar, 
  ChevronRight, DollarSign, AlertCircle, CheckCircle,
  ShoppingBag, Edit2, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import MobileNav from '../components/MobileNav';
import AddSpendingModal from '../components/AddSpendingModal';
import EditSpendingModal from '../components/EditSpendingModal';
import { formatDashboardStatus } from '../utils/spendingStatus';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

// Use window.location.origin for API calls to work on any domain
const API = `${window.location.origin}/api`;

export default function DashboardPage() {
  const { user } = useAuth();
  const currency = user?.currency || 'GBP';
  const [forecast, setForecast] = useState(null);
  const [spending, setSpending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSpendingModal, setShowSpendingModal] = useState(false);
  const [editingSpending, setEditingSpending] = useState(null);

  const fetchData = async () => {
    try {
      const [forecastRes, spendingRes] = await Promise.all([
        fetch(`${API}/forecast`, { credentials: 'include' }),
        fetch(`${API}/spending`, { credentials: 'include' })
      ]);
      
      if (forecastRes.ok) {
        const data = await forecastRes.json();
        setForecast(data);
      }
      if (spendingRes.ok) {
        const data = await spendingRes.json();
        setSpending(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDeleteSpending = async (spendingId) => {
    try {
      const response = await fetch(`${API}/spending/${spendingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Spending deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatSpendingDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Use the shared status utility
  const status = formatDashboardStatus(forecast?.safe_to_spend_today);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1224] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#63E6E2]/30 border-t-[#63E6E2] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1224] page-container" data-testid="dashboard-page">
      {/* Header */}
      <header className="px-4 pt-4 pb-2 safe-top">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[#6B7280] text-sm">Welcome back,</p>
            <h1 className="font-heading text-xl font-bold text-white">{user?.name || 'User'}</h1>
          </div>
          <Link to="/settings">
            <div className="w-10 h-10 rounded-full bg-[#1B1F3A] border border-[#2D3354] flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-white font-medium">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
              )}
            </div>
          </Link>
        </div>
      </header>

      <main className="px-4 py-4 space-y-6">
        {/* Safe to Spend Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative bg-[#1B1F3A] rounded-2xl border border-[#2D3354] p-6 overflow-hidden"
          data-testid="safe-to-spend-card"
        >
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#63E6E2] to-transparent" />
          
          <div className="mb-4">
            <span className="text-[#6B7280] text-xs uppercase tracking-wider font-medium">Safe to Spend Today</span>
          </div>
          
          {/* Main Amount */}
          <div className="text-center mb-4">
            <span className="font-heading text-5xl sm:text-6xl font-black text-[#63E6E2] number-display" data-testid="safe-to-spend-amount">
              {formatCurrency(forecast?.safe_to_spend_today || 0, currency)}
            </span>
          </div>

          {/* Status Indicator */}
          <div className="text-center mb-6" data-testid="spending-status-indicator">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-xl" role="img" aria-label={status.label}>{status.emoji}</span>
              <span className={`font-semibold ${status.textColor}`}>{status.label}</span>
            </div>
            <p className="text-[#9CA3AF] text-sm">{status.message}</p>
          </div>
          
          {/* Stats Row: Balance | Bills Due | Days Left */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-[#2D3354]">
            <div className="text-center">
              <p className="text-[#6B7280] text-xs mb-1">Balance</p>
              <p className="text-white font-semibold" data-testid="balance-display">{formatCurrency(forecast?.remaining_balance || 0, currency)}</p>
            </div>
            <div className="text-center">
              <p className="text-[#6B7280] text-xs mb-1">Bills Due</p>
              <p className="text-white font-semibold" data-testid="bills-due-display">{formatCurrency(forecast?.total_bills_due || 0, currency)}</p>
            </div>
            <div className="text-center">
              <p className="text-[#6B7280] text-xs mb-1">Days Left</p>
              <p className="text-white font-semibold" data-testid="days-left-display">{forecast?.days_until_payday || 0}</p>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex gap-3"
        >
          <Button
            onClick={() => setShowSpendingModal(true)}
            className="flex-1 h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold"
            data-testid="add-spending-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Spending
          </Button>
          <Link to="/bills" className="flex-1">
            <Button variant="outline" className="w-full h-12 border-[#2D3354] text-white hover:bg-white/5" data-testid="view-bills-btn">
              <Receipt className="w-5 h-5 mr-2" />
              Bills
            </Button>
          </Link>
        </motion.div>

        {/* Upcoming Bills - Compact list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-base font-semibold text-white">Upcoming Bills</h2>
            <Link to="/bills" className="text-[#63E6E2] text-sm font-medium flex items-center">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {forecast?.upcoming_bills?.length > 0 ? (
            <div className="space-y-2">
              {forecast.upcoming_bills.slice(0, 3).map((bill, index) => (
                <div key={bill.bill_id || index} className="flex items-center justify-between py-2 px-3 bg-[#252A4A] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#2D3354] flex items-center justify-center">
                      <Receipt className="w-4 h-4 text-[#9CA3AF]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{bill.name}</p>
                      <p className="text-[#6B7280] text-xs">Due: {bill.due_date}th</p>
                    </div>
                  </div>
                  <span className="text-white text-sm font-semibold">{formatCurrency(bill.amount, currency)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-[#9CA3AF] text-sm">No upcoming bills</p>
            </div>
          )}
        </motion.div>

        {/* Unplanned Spending */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-heading text-lg font-bold text-white">Unplanned Spending</h2>
            <Link to="/spending" className="text-[#63E6E2] text-sm font-medium flex items-center">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-[#6B7280] text-sm mb-4">Expenses added outside your regular bills</p>
          
          {spending.length > 0 ? (
            <div className="space-y-2">
              {spending.slice(0, 3).map((item) => (
                <div 
                  key={item.spending_id} 
                  className="flex items-center justify-between p-3 bg-[#252A4A] rounded-lg"
                  data-testid={`dashboard-spending-${item.spending_id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-[#2D3354] flex items-center justify-center flex-shrink-0">
                      <ShoppingBag className="w-5 h-5 text-[#9CA3AF]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium truncate">{item.description}</p>
                      <p className="text-[#6B7280] text-sm">{formatSpendingDate(item.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className="text-white font-semibold">{formatCurrency(item.amount, currency)}</span>
                    <button
                      onClick={() => setEditingSpending(item)}
                      className="p-1.5 text-[#6B7280] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                      data-testid={`edit-spending-${item.spending_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSpending(item.spending_id)}
                      className="p-1.5 text-[#6B7280] hover:text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg transition-colors"
                      data-testid={`delete-spending-${item.spending_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#252A4A] flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-[#6B7280]" />
              </div>
              <p className="text-[#9CA3AF]">No spending recorded yet</p>
            </div>
          )}
        </motion.div>

        {/* Payday Info */}
        {forecast?.pay_schedule && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
            data-testid="payday-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#63E6E2]/10 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#63E6E2]" />
              </div>
              <div>
                <p className="text-[#6B7280] text-sm">Next Payday</p>
                <p className="text-white font-semibold">
                  {new Date(forecast.pay_schedule.next_payday).toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'short'
                  })}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </main>

      {/* Mobile Navigation */}
      <MobileNav />

      {/* Add Spending Modal */}
      <AddSpendingModal 
        open={showSpendingModal} 
        onClose={() => setShowSpendingModal(false)}
        onSuccess={() => {
          fetchData();
          setShowSpendingModal(false);
        }}
      />

      {/* Edit Spending Modal */}
      <EditSpendingModal
        open={!!editingSpending}
        spending={editingSpending}
        onClose={() => setEditingSpending(null)}
        onSuccess={() => {
          fetchData();
          setEditingSpending(null);
        }}
      />
    </div>
  );
}
